import { Injectable } from '@angular/core';
import { EnvironmentService } from '@services/environment.service';
import { HttpUtilService } from '@services/http-util.service';
import { BehaviorSubject, Observable, of, Subscription, Subject } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { NotificationsService } from './notifications.service';
import { WebSocketService } from './web-socket.service';
import { TermsService } from '@app/admin/services/terms.service';
import { TranslationService } from './translation.service';

@Injectable({
  providedIn: 'root',
})
export class IntegrationService {
  private _integrationsState: BehaviorSubject<IntegrationsState> =
    new BehaviorSubject({
      integrations: [],
      isLoading: true,
      hasError: false,
    });
  public initialIntegrationLoad = true;
  private socketSubscription: Subscription;
  private integrationChanges: Subject<any> = new Subject();

  private noUrlIntegrations = ['raisers_edge'];

  constructor(
    private termsService: TermsService,
    private notification: NotificationsService,
    private http: HttpUtilService,
    private environmentService: EnvironmentService,
    private socketService: WebSocketService,
    private translationService: TranslationService
  ) {
    this.environmentService
      .watchTypeChange()
      .pipe(catchError(error => of({ error })))
      .subscribe(response => {
        if (response) {
          if (!this.initialIntegrationLoad) {
            this._integrationsState.next({
              integrations: [],
              isLoading: true,
              hasError: false,
            });
          }
        }
      });
  }

  getIntegrations$(): Observable<IntegrationsState> {
    if (!this.socketSubscription) {
      this.socketSubscription = this.socketService
        .subscribeToMessages('integration')
        .subscribe(this.updatesFromSocket.bind(this));
    }
    this.getIntegrationsNetworkCall$().subscribe();
    return this._integrationsState.asObservable();
  }

  /**tigger for single integration changes */
  watchIntegration$() {
    return this.integrationChanges.asObservable();
  }

  getIntegrationNetworkCall$(id: number) {
    return this.http
      .request(
        'get',
        `${this.environmentService.environment.apiRoot}/integrations?read=json_data&key_values=created&id=${id}`,
        null,
        null,
        true
      )
      .pipe(catchError(error => of({ error })))
      .pipe(
        map(response => {
          // An error response, or a missing/deleted row, must not produce a
          // placeholder integration: it would end up in the shared list with no
          // integration_type and break anything rendering it.
          if (!response?.[0]?.id) return null;
          const integrationsState = this._integrationsState.getValue();
          const integration = this.mapIntegration(response[0]);
          integration.generatedRequirements =
            this.checkRequirements(integration);
          integration.generatedStatus = this.getStatus(integration);
          integration.generatedState = this.getState(integration);
          const exists = integrationsState.integrations.some(
            _integration => _integration.id === integration.id
          );
          this._integrationsState.next({
            ...integrationsState,
            integrations: exists
              ? integrationsState.integrations.map(_integration =>
                  _integration.id === integration.id ? integration : _integration
                )
              : [integration, ...integrationsState.integrations],
          });
          return integration;
        })
      );
  }

  getIntegrationsNetworkCall$() {
    return this.http
      .request(
        'get',
        `${this.environmentService.environment.apiRoot}/integrations?read=json_data&key_values=created`,
        null,
        null,
        true
      )
      .pipe(catchError(error => of({ error })))
      .pipe(
        tap((response: any) => {
          let integrations;
          if (Array.isArray(response)) {
            response.forEach(integration => {
              if (integration.integration_type === 'dwh') {
                integration.json_data.friendly_name = 'Data Warehouse';
                integration.json_data.url = 'Internal';
                integration.json_data.dwh_type = 'Warehouse';
              }
              if (integration.integration_type === 'live_event') {
                integration.json_data.friendly_name = 'Live Event';
                integration.json_data.url = 'Internal';
                integration.json_data.live_event_type = 'Live Event';
              }
              integration.generatedState = this.getState(integration);
              integration.generatedRequirements =
                this.checkRequirements(integration);
              integration.generatedStatus = this.getStatus(integration);
            });
            integrations = response.map(this.mapIntegration);
          } else {
            integrations = [];
          }
          const hasError = !!response?.error || !Array.isArray(response);
          this._integrationsState.next({
            isLoading: false,
            integrations,
            hasError,
          });
        })
      );
  }
  /**
   * Check if the Canvas Data Service is enabled for the integration
   * @param integrationId - The ID of the integration
   * @returns true if the Canvas Data Service is enabled, false otherwise
   */
  checkCanvasDataServiceEnabled(integrationId) {
    const state = this._integrationsState.getValue();
    let integration;
    if (!state.isLoading) {
      integration = state.integrations.find(
        integration => Number(integration.id) === Number(integrationId)
      );
    }
    return integration?.json_data?.liveevents_process?.status === 'ok';
  }

  getIntegration$(id): Observable<any> {
    // hopefully will be understood that no id means no integration..
    if (!id) return of({ isLoading: false });
    const state = this._integrationsState.getValue();
    let integration;
    if (!state.isLoading) {
      integration = state.integrations.find(
        integration => integration.id == id
      );
    }
    if (!integration) {
      return this.getIntegrationNetworkCall$(id);
    } else {
      return of(integration);
    }
  }

  addPausedImportSet(integrationId, batchId) {
    const integrations = this._integrationsState.getValue().integrations;
    const updatedIntegrations = integrations.map(_integration => {
      if (_integration.id !== integrationId) return _integration;
      const pausedSets = Array.isArray(_integration?.json_data?.paused_sets)
        ? [batchId, ..._integration.json_data.paused_sets]
        : [batchId];
      return {
        ..._integration,
        json_data: {
          ..._integration.json_data,
          paused_sets: pausedSets,
        },
      };
    });
    this._integrationsState.next({
      ...this._integrationsState.getValue(),
      integrations: updatedIntegrations,
    });
  }

  removePausedImportSet(integrationId, batchId) {
    const integrations = this._integrationsState.getValue().integrations;
    const updatedIntegrations = integrations.map(_integration => {
      if (_integration.id !== integrationId) return _integration;
      return {
        ..._integration,
        json_data: {
          ..._integration.json_data,
          paused_sets: _integration.json_data.paused_sets.filter(
            _batchId => _batchId !== batchId
          ),
        },
      };
    });
    this._integrationsState.next({
      ...this._integrationsState.getValue(),
      integrations: updatedIntegrations,
    });
  }

  updatesFromSocket(event) {
    const { id, updates } = event;
    const updatedIntegrations = this._integrationsState.getValue();
    let _integration = updatedIntegrations.integrations.find(
      _integration => _integration.id === id
    );
    let json_data = _integration.json_data;
    Object.assign(_integration, updates);
    Object.assign(json_data, updates.json_data);
    _integration.json_data = json_data;
    // update the generated values
    _integration.generatedState = this.getState(_integration);
    if (_integration.generatedState === 'Active') {
      _integration.isRefreshing = false;
    }
    _integration.generatedRequirements = this.checkRequirements(_integration);
    _integration.generatedStatus = this.getStatus(_integration);
    this.integrationChanges.next(_integration);
  }

  mapIntegration(data): IntegrationState {
    return { ...data, isUpdating: false };
  }

  updateIntegrationPause$(integration, setToResume) {
    const integrationsState = this._integrationsState.getValue();
    let _integration = integrationsState.integrations.find(
      _integration => _integration.id === integration.id
    );
    _integration.isUpdating = true;
    this._integrationsState.next(integrationsState);

    const toggleState = setToResume ? 'resume' : 'pause';

    return this.http
      .request(
        'PUT',
        `${this.environmentService.environment.apiRoot}/integrations/${integration.integration_type}/${integration.name}/${toggleState}`,
        null,
        null,
        true
      )
      .pipe(catchError(error => of({ error })))
      .pipe(
        tap((response: any) => {
          let hasError =
            response.error || response.errorMessage || !response?.[0]?.id;
          const integrations = this._integrationsState.getValue().integrations;
          if (hasError) {
            this._integrationsState.next({
              ...this._integrationsState.getValue(),
              integrations: integrations.map(_integration => {
                if (_integration.id !== integration.id) return _integration;
                return {
                  ..._integration,
                  isUpdating: false,
                  generatedState: this.getState(_integration),
                  generatedRequirements: this.checkRequirements(_integration),
                  generatedStatus: this.getStatus(_integration),
                };
              }),
            });
            this.notification.popNotification(
              '',
              this.translationService.getTranslatedContent(
                'error_updating_integration',
                'Error Updating Integration'
              ) + `: ${integration?.json_data.friendly_name}`
            );
          }

          this._integrationsState.next({
            ...this._integrationsState.getValue(),
            integrations: integrations.map(_integration => {
              if (_integration.id !== integration.id) return _integration;
              const updated = {
                ..._integration,
                updated_at: new Date().toISOString(),
                paused: !setToResume,
                isUpdating: false,
              };
              updated.generatedState = this.getState(updated);
              updated.generatedRequirements = this.checkRequirements(updated);
              updated.generatedStatus = this.getStatus(updated);
              return updated;
            }),
          });
        })
      );
  }

  refreshIntegration$(integration: IntegrationState) {
    const toCheck = ['canvas', 'brightspace'];
    const integrationsState = this._integrationsState.getValue();
    let _integration = integrationsState.integrations.find(
      _integration => _integration.id === integration.id
    );
    let type = _integration.integration_type;
    // only kick off refresh if the integration has required checks
    if (toCheck.includes(_integration.json_data[type + '_type'])) {
      _integration.isRefreshing = true;
      _integration.generatedState = 'Processing';
      _integration.generatedStatus = 'processing';
      _integration.generatedRequirements = null;
      this._integrationsState.next(integrationsState);
      return this.http
        .request(
          'PUT',
          `${this.environmentService.environment.apiRoot}/integrations/${integration.integration_type}/${integration.name}/refresh`,
          null,
          null,
          true
        )
        .pipe(catchError(error => of({ error })))
        .pipe(
          tap((response: any) => {
            // give about 5 seconds before able to refresh again and time for the integration to start processing and send back socket messages
            setTimeout(() => {
              _integration.isRefreshing = false;
            }, 5000);
          })
        );
    }
  }

  updateIntegration$(
    integration: IntegrationState,
    jsonDataUpdates: JsonDataUpdates
  ) {
    const integrations = this._integrationsState.getValue().integrations;
    this._integrationsState.next({
      ...this._integrationsState.getValue(),
      integrations: integrations.map(_integration => {
        if (_integration.id !== integration.id) return _integration;
        return {
          ..._integration,
          isUpdating: true,
        };
      }),
    });
    return this.http
      .request(
        'PUT',
        `${this.environmentService.environment.apiRoot}/integrations/${integration.integration_type}/${integration.name}`,
        { json_data: jsonDataUpdates, id: integration.id },
        null,
        true
      )
      .pipe(catchError(error => of({ error })))
      .pipe(
        tap((response: any) => {
          const integrations = this._integrationsState.getValue().integrations;
          let hasError =
            response.error || response.errorMessage || !response?.[0]?.id;
          if (hasError) {
            this._integrationsState.next({
              ...this._integrationsState.getValue(),
              integrations: integrations.map(_integration => {
                if (_integration.id !== integration.id) return _integration;
                return {
                  ..._integration,
                  isUpdating: false,
                  generatedRequirements: this.checkRequirements(_integration),
                  generatedStatus: this.getStatus(_integration),
                  generatedState: this.getState(_integration),
                };
              }),
            });

            return this.notification.popNotification(
              '',
              `Error Updating Integration: ${integration?.json_data.friendly_name}`
            );
          }
          this._integrationsState.next({
            ...this._integrationsState.getValue(),
            integrations: integrations.map(_integration => {
              if (_integration.id !== integration.id) return _integration;

              const updatedJsonData =
                !hasError && jsonDataUpdates
                  ? { ..._integration.json_data, ...jsonDataUpdates }
                  : _integration.json_data;

              if (this.termsService.hasChanges) {
                updatedJsonData.termsToPull =
                  this.termsService._termChanges.current;
              }

              const updated = {
                ..._integration,
                updated_at: new Date().toISOString(),
                json_data: updatedJsonData,
                isUpdating: false,
              };
              updated.generatedRequirements = this.checkRequirements(updated);
              updated.generatedStatus = this.getStatus(updated);
              updated.generatedState = this.getState(updated);
              return updated;
            }),
          });
        })
      );
  }

  getNewIntegrationName() {
    const instanceDetails = this.environmentService.environment.domain.replace(
      /[^a-zA-Z0-9]/g,
      ''
    );
    const randomId = Date.now().toString() + Math.floor(Math.random() * 1000);
    return `${instanceDetails.substring(0, 5)}_${this._integrationsState.getValue().integrations.length + 1}_${randomId}`;
  }

  duplicateIntegration$(integration, friendlyName, name?: string) {
    return this.createIntegrationNetworkCall$(
      name || this.getNewIntegrationName(),
      integration.integration_type,
      {
        friendly_name: friendlyName,
      },
      null,
      `copy_from=${integration.id}`
    );
  }

  deleteIntegration$(id) {
    return this.http
      .request(
        'delete',
        this.environmentService.environment.apiRoot +
          '/integrations' +
          `?id=${id}`
      )
      .pipe(catchError(error => of(error)))
      .pipe(
        tap((response: any) => {
          if (response?.status === 'ok') {
            let { integrations } = this._integrationsState.getValue();
            integrations = integrations.filter(
              integration => integration.id !== id
            );
            this._integrationsState.next({
              ...this._integrationsState.getValue(),
              integrations,
            });
          }
        })
      );
  }

  createIntegrationNetworkCall$(
    name,
    type,
    data,
    partnerId = null,
    additionalQueryParams?
  ) {
    let params = new URLSearchParams();
    if (additionalQueryParams) {
      additionalQueryParams.split('&').forEach(param => {
        let [key, value] = param.split('=');
        params.append(key, value);
      });
    }
    if (partnerId) {
      params.append('partnerId', partnerId);
    }
    let integrationUrl = `${
      this.environmentService.environment.apiRoot
    }/integrations/${type}/${name}?${params.toString().trim()}`;

    return this.http
      .request('post', integrationUrl, { json_data: data }, null, true)
      .pipe(catchError(error => of({ error })))

      .pipe(
        switchMap((response: any) => {
          if (!response?.id) return of(response);
          return this.http
            .request(
              'get',
              `${this.environmentService.environment.apiRoot}/integrations?read=json_data&key_values=created&id=${response.id}`
            )
            .pipe(catchError(error => of({ error })))
            .pipe(
              map(response => {
                if (!response?.[0]?.id) return null;
                const integrationsState = this._integrationsState.getValue();
                const integration = this.mapIntegration(response[0]);
                integration.generatedRequirements =
                  this.checkRequirements(integration);
                integration.generatedStatus = this.getStatus(integration);
                integration.generatedState = this.getState(integration);
                this._integrationsState.next({
                  ...integrationsState,
                  integrations: [
                    integration,
                    ...integrationsState.integrations,
                  ],
                });
                return integration;
              })
            );
        })
      );
  }

  getStatusRequirements(integration) {
    let scaffoldType = this.environmentService.currentType;
    let type =
      integration?.json_data?.lms_type ||
      integration?.json_data?.sis_type ||
      integration?.json_data?.crm_type;
    return StatusItems.filter(
      item =>
        item.lms.includes(type) && item.scaffoldType.includes(scaffoldType)
    );
  }

  checkRequirements(integration) {
    let requirements = this.getStatusRequirements(integration);
    let failed = requirements.map(status => {
      let result;
      if (status.key) {
        if (status.ifPresent) {
          result = integration?.json_data?.[status.key] || {
            status: 'ok',
            message: status.messages?.error || 'Check has not been run',
          };
        } else {
          result = integration?.json_data?.[status.key] || {
            status: 'error',
            message: status.messages?.error || 'Check has not been run',
          };
        }
        result.title = status.title;
      } else if (status.dataKeys) {
        let found = status.dataKeys.every(key => integration?.json_data?.[key]);
        let state = found ? 'ok' : 'error';
        result = {
          status: state,
          title: status.title,
          message: status.messages[state],
        };
      }
      if (result.status === 'error') {
        return result;
      }
    });
    return failed?.find(status => status?.status === 'error');
  }

  getState(integration) {
    const systemType =
      integration?.json_data?.[integration?.integration_type + '_type'] ||
      'none';
    let requirements =
      integration.generatedRequirements || this.checkRequirements(integration);
    if (
      integration?.json_data &&
      !integration?.json_data?.message &&
      ['canvas', 'brightspace'].includes(integration.json_data.lms_type) &&
      !integration.json_data.status
    ) {
      integration.json_data.status = 'processing';
      integration.json_data.message = 'Processing';
    }
    if (requirements?.title) {
      return requirements?.title;
    }
    if (requirements?.message) {
      return requirements?.message;
    }
    if (integration.integration_type === 'dwh') {
      return 'Active';
    }
    if (integration.integration_type === 'live_event') {
      return 'Active';
    }
    if (
      !integration.json_data?.url &&
      !this.noUrlIntegrations.includes(systemType)
    ) {
      return this.translationService.getTranslatedContent(
        'awaiting_system_configuration',
        'Awaiting System Configuration'
      );
    }
    if (
      integration.integration_type == 'lms' &&
      (!integration.json_data?.utilUsername ||
        !integration.json_data?.utilPassword)
    ) {
      return this.translationService.getTranslatedContent(
        'awaiting_credentials',
        'Awaiting Credentials'
      );
    }
    if (integration.json_data?.status === 'processing') {
      return 'Processing';
    }
    if (integration.json_data?.status === 'error') {
      return (
        integration.json_data.message ||
        this.translationService.getTranslatedContent(
          'error_with_configuration',
          'Error with Configuration'
        )
      );
    }
    return integration.paused ? 'Paused' : 'Active';
  }

  getStatus(integration) {
    switch (integration?.json_data?.status) {
      case 'processing':
        return 'processing';
      case 'ok':
        if (['Active', 'Paused'].includes(integration?.generatedState)) {
          return 'valid';
        }
        return 'offline';
      case 'failed':
        return 'error';
      default:
        if (['Active', 'Paused'].includes(integration?.generatedState)) {
          return 'valid';
        }
        return 'offline';
    }
  }
}

export interface JsonDataTerm {
  name: string;
  term_id: number;
  sis_term_id: string;
  k16_canvas_id: string;
  last_imported: string;
  integration_id: string;
  workflow_state: string;
}

export interface Term {
  id: number;
  json_data: JsonDataTerm;
}

export interface TermsProcess {
  status: string;
  message: string;
}

export interface TokensProcess {
  status: string;
  message: string;
}

export interface JsonData {
  url: string;
  status: string;
  message: string;
  lms_type: string;
  termsToPull: Term[];
  utilPassword: string;
  utilUsername: string;
  friendly_name: string;
  terms_process: TermsProcess;
  data_direction: string;
  sub_account_id: number;
  tokens_process: TokensProcess;
  integration_type: string;
  paused_sets?: number[];
}

export interface JsonDataUpdates {
  url?: string;
  status?: string;
  message?: string;
  lms_type?: string;
  termsToPull?: Term[];
  utilPassword?: string;
  utilUsername?: string;
  friendly_name?: string;
  terms_process?: TermsProcess;
  data_direction?: string;
  sub_account_id?: number;
  tokens_process?: TokensProcess;
  integration_type?: string;
  paused_sets?: number[];
}

export interface Integration {
  id: string;
  name: string;
  paused: boolean;
  integration_type: string;
  institution_id: string;
  institution_name: string;
  created_at: string;
  json_data: JsonData;
  updated_at: string;
  generatedState: string;
  generatedRequirements: any;
  generatedStatus: string;
}

export interface IntegrationState extends Integration {
  isUpdating: boolean;
  isRefreshing: boolean;
}

export interface IntegrationsState {
  integrations: IntegrationState[];
  isLoading: boolean;
  hasError: boolean;
}

export enum LmsType {
  Blackboard = 'blackboard',
  Brightspace = 'brightspace',
  Canvas = 'canvas',
  Jenzabar = 'jenzabar',
  Moodle = 'moodle',
  Sakai = 'sakai',
  Schoology = 'schoology',
}

export const StatusItems = [
  {
    title: 'Integration URL Set',
    dataKeys: ['url'],
    lms: [
      'canvas',
      'moodle',
      'blackboard',
      'sakai',
      'schoology',
      'brightspace',
      'banner',
      'workday',
      'recruit',
    ],
    scaffoldType: [
      'migration',
      'archivev2',
      'archive',
      'datax',
      'ai-detection',
    ],
    messages: {
      ok: 'URL is set',
      error: 'URL is missing',
    },
  },
  {
    key: 'url_check',
    title: 'Validate URL',
    lms: ['canvas', 'brightspace'],
    scaffoldType: [
      'migration',
      'archivev2',
      'archive',
      'datax',
      'ai-detection',
    ],
    messages: {
      ok: 'URL is valid',
      error: 'URL can not be validated',
    },
  },
  {
    title: 'Utility Account Credentials',
    dataKeys: ['utilUsername', 'utilPassword'],
    lms: [
      'canvas',
      'moodle',
      'blackboard',
      'sakai',
      'schoology',
      'brightspace',
      'banner',
      'workday',
      'recruit',
    ],
    scaffoldType: [
      'migration',
      'archivev2',
      'archive',
      'datax',
      'ai-detection',
    ],
    messages: {
      ok: 'Utility account credentials are set',
      error: 'Utility account credentials are missing',
    },
  },
  {
    key: 'utility_account_mfa',
    title: 'Validate Utility Account MFA if needed',
    ifPresent: true,
    lms: ['brightspace', 'canvas'],
    scaffoldType: [
      'migration',
      'archivev2',
      'archive',
      'datax',
      'ai-detection',
    ],
    messages: {
      ok: 'Utility account MFA is not set up or configured',
      error: 'Utility account MFA is not configured correctly',
    },
  },
  {
    key: 'utility_account_check',
    title: 'Validate Utility Account Credentials',
    lms: ['canvas', 'brightspace'],
    scaffoldType: [
      'migration',
      'archivev2',
      'archive',
      'datax',
      'ai-detection',
    ],
    messages: {
      ok: 'Utility account credentials are valid',
      error: 'Utility account credentials can not be validated as entered',
    },
  },
  {
    key: 'user_account_match',
    title: 'Validate Utility Account Credentials match Tool Account',
    lms: ['brightspace'],
    scaffoldType: [
      'migration',
      'archivev2',
      'archive',
      'datax',
      'ai-detection',
    ],
    messages: {
      ok: 'Utility account credentials match Tool account',
      error: 'Utility account credentials do not match Tool account',
    },
  },
  {
    key: 'tokens_process',
    title: 'Validate/Generate Tokens',
    lms: ['canvas'],
    scaffoldType: [
      'migration',
      'archivev2',
      'archive',
      'datax',
      'ai-detection',
    ],
    messages: {
      ok: 'Tokens validated or generated successfully',
      error:
        'Tokens can not be validated or generated, check the entered credentials',
    },
  },
  {
    key: 'app_context',
    title: 'Brightspace App Setup',
    lms: ['brightspace'],
    scaffoldType: [
      'migration',
      'archivev2',
      'archive',
      'datax',
      'ai-detection',
    ],
    messages: {
      ok: 'Brightspace App Setup is valid',
      error: 'Brightspace App Setup is invalid, check App/User ID and Key',
    },
  },
  {
    key: 'verify_subaccount',
    title: 'Verify Subaccount',
    lms: ['canvas'],
    scaffoldType: [
      'migration',
      'archivev2',
      'archive',
      'datax',
      'ai-detection',
    ],
    messages: {
      ok: 'Subaccount verified successfully',
      error: 'Subaccount can not be verified, check the ID entered',
    },
  },
  {
    key: 'root_details',
    title: 'Get Root Details',
    lms: ['canvas'],
    scaffoldType: [
      'migration',
      'archivev2',
      'archive',
      'datax',
      'ai-detection',
    ],
    messages: {
      ok: 'Root details retrieved successfully',
      error: 'Root details can not be retrieved with provided permissions',
    },
  },
  {
    key: 'permissions_process',
    title: 'Validate Permissions',
    lms: ['canvas', 'brightspace'],
    scaffoldType: [
      'migration',
      'archivev2',
      'archive',
      'datax',
      'ai-detection',
    ],
    messages: {
      ok: 'Permissions validated successfully',
      error: 'Missing or incorrect permissions on utility account',
    },
  },
  {
    key: 'terms_process',
    title: 'Get Terms',
    lms: ['canvas'],
    scaffoldType: ['archivev2', 'datax', 'ai-detection'],
    messages: {
      ok: 'Terms retrieved successfully',
      error:
        'Terms can not be retrieved, check the utility account permissions',
    },
  },
];
